import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Loader2, X, Pencil } from "lucide-react";
import UserAvatar from "@/components/shared/Avatar";
import { useSearchPeopleLazyQuery } from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { isLovablePreview } from "@/lib/utils/environment";
import { getDisplayName } from "@/lib/utils/display-name";
import { people as allPeople } from "@/data/dummy";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

export interface ContactDetails {
  id: string;
  name: string;
  email?: string | null;
}

interface EditableContactsProps {
  value: ContactDetails[];
  onChange: (contacts: ContactDetails[]) => void;
  onDrillDown?: (contact: ContactDetails) => void;
  placeholder?: string;
  className?: string;
}

export default function EditableContacts({
  value,
  onChange,
  onDrillDown,
  placeholder = "-",
  className,
}: EditableContactsProps) {
  const { t } = useTranslation();
  // Local working copy so add/remove reflect instantly while the mutation persists.
  const [contacts, setContacts] = useState<ContactDetails[]>(value);
  const [editing, setEditing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [results, setResults] = useState<ContactDetails[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const workspaceId = useWorkspaceId();

  const [searchPeople, { loading }] = useSearchPeopleLazyQuery();

  // Keep local copy in sync when the parent supplies fresh data (e.g. after refetch).
  useEffect(() => {
    if (!editing) setContacts(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing]);

  // Close on outside click — check both container and fixed dropdown.
  useEffect(() => {
    if (!editing) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setEditing(false);
        setShowDropdown(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [editing]);

  const fetchContacts = useCallback(async (search: string) => {
    if (isLovablePreview()) {
      const filtered = allPeople
        .filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 10)
        .map(p => ({ id: p.id, name: p.name, email: p.email }));
      setResults(filtered);
      setShowDropdown(true);
      return;
    }

    if (!search.trim()) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    try {
      const result = await searchPeople({
        variables: { workspaceId: String(workspaceId), search: search.trim(), first: 10 },
      });
      const found = result.data?.contacts?.edges
        ?.map(edge => edge?.node)
        .filter(Boolean)
        .map(c => ({
          id: c!.id,
          name: getDisplayName(c!.properties?.firstName, c!.properties?.lastName, c!.primaryEmail) || c!.primaryEmail || "Unknown",
          email: c!.primaryEmail,
        })) || [];
      setResults(found);
      setShowDropdown(true);
    } catch (error) {
      console.error("Error searching contacts:", error);
      setResults([]);
      setShowDropdown(false);
    }
  }, [workspaceId, searchPeople]);

  // Debounced search.
  useEffect(() => {
    if (!editing) return;
    if (!searchText.trim()) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    const timer = setTimeout(() => fetchContacts(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText, editing, fetchContacts]);

  // Fixed-position dropdown to escape overflow/stacking-context clipping.
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  useEffect(() => {
    if (!editing || !showDropdown || !inputWrapperRef.current) return;
    const updatePos = () => {
      const rect = inputWrapperRef.current?.getBoundingClientRect();
      if (rect) setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 200) });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [editing, showDropdown]);

  const addContact = (contact: ContactDetails) => {
    if (contacts.find(c => c.id === contact.id)) {
      setSearchText("");
      return;
    }
    const next = [...contacts, contact];
    setContacts(next);
    onChange(next);
    setSearchText("");
    setShowDropdown(false);
  };

  const removeContact = (id: string) => {
    const next = contacts.filter(c => c.id !== id);
    setContacts(next);
    onChange(next);
  };

  if (editing) {
    return (
      <div className={cn("flex flex-col gap-1.5 text-sm relative min-w-[160px]", className)} ref={containerRef}>
        {contacts.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {contacts.map(c => (
              <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-muted pl-1 pr-1.5 py-0.5 text-xs">
                <UserAvatar name={c.name} size="xs" />
                <span className="max-w-[120px] truncate">{c.name}</span>
                <button
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); removeContact(c.id); }}
                  className="text-muted-foreground hover:text-foreground transition"
                  aria-label={`Remove ${c.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="relative" ref={inputWrapperRef}>
          <input
            ref={inputRef}
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setEditing(false);
                setShowDropdown(false);
                setSearchText("");
              }
            }}
            placeholder={t("grid.cell.addContact")}
            className="w-full bg-transparent border-b border-accent outline-none text-sm py-0 px-0"
          />
          {loading && (
            <div className="absolute right-0 top-0">
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            </div>
          )}
          {showDropdown && dropdownPos && results.length > 0 && (
            <div
              ref={dropdownRef}
              className="fixed z-[100] rounded-md border border-border bg-popover shadow-md py-1 max-h-48 overflow-y-auto"
              style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
            >
              {results.map(contact => (
                <button
                  key={contact.id}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); addContact(contact); }}
                  disabled={!!contacts.find(c => c.id === contact.id)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition flex items-center gap-2 disabled:opacity-40 disabled:cursor-default"
                >
                  <UserAvatar name={contact.name} size="xs" />
                  <span className="flex-1 truncate">{contact.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const primary = contacts[0];
  const extra = contacts.slice(1);

  return (
    <div className={cn("flex items-center gap-2 text-sm group", className)}>
      {primary ? (
        <>
          <UserAvatar name={primary.name} size="xs" />
          <button
            className="hover:text-accent transition text-left truncate max-w-[140px]"
            onClick={() => onDrillDown?.(primary)}
          >
            {primary.name}
          </button>
          {extra.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <button className="shrink-0 text-[10px] font-medium bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 leading-none hover:bg-accent hover:text-accent-foreground transition">
                  +{extra.length}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-48 p-1">
                {extra.map(c => (
                  <button
                    key={c.id}
                    className="flex items-center gap-2 w-full rounded-md px-2.5 py-2 text-sm hover:bg-muted/50 transition text-left"
                    onClick={() => onDrillDown?.(c)}
                  >
                    <UserAvatar name={c.name} size="xs" />
                    <span className="truncate">{c.name}</span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
        </>
      ) : (
        <span className="text-muted-foreground">{placeholder}</span>
      )}
      <Pencil
        onClick={() => setEditing(true)}
        className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition shrink-0 cursor-pointer"
      />
    </div>
  );
}
